import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {buildInteractionIdProps} from '../../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useConstant} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ConversationSortPropertiesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {
  CellsContext,
  CellsContextTypesEnum,
} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {CellsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {isExpandedDarkTheme} from '../../../../../libs/shared/ui/src/theme/themeTypeHelpers';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import inboxZero from '../../../../assets/images/inboxZero.svg';
import inboxZeroCustomViews from '../../../../assets/images/inboxZeroCustomViews.png';
import inboxZeroPoster1 from '../../../../assets/images/inboxZeroPoster1.png';
import inboxZeroPoster2 from '../../../../assets/images/inboxZeroPoster2.png';
import inboxZeroAnimatedMov1 from '../../../../assets/videos/inboxZeroAnimated1.mov';
import inboxZeroAnimated1 from '../../../../assets/videos/inboxZeroAnimated1.mp4';
import inboxZeroAnimatedWebm1 from '../../../../assets/videos/inboxZeroAnimated1.webm';
import inboxZeroAnimatedMov2 from '../../../../assets/videos/inboxZeroAnimated2.mov';
import inboxZeroAnimated2 from '../../../../assets/videos/inboxZeroAnimated2.mp4';
import inboxZeroAnimatedWebm2 from '../../../../assets/videos/inboxZeroAnimated2.webm';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {findThemedImageInfo, ThemedImageNamesEnum} from '../../../../helpers/imageThemeHelpers';
import {LensModel} from '../../../../models/api/lensModel';
import {StartGuideTaskTypesEnum} from '../../../../models/api/preferencesModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {emojiMap} from '../../../../services/emojis/emojis';
import {getIsBackfillingCustomView} from '../../../../store/data/jobs/jobsSelectors';
import {CellsPanelSearchAppliedQuerySuggestion} from '../../../../store/panels/cells/search/cellsPanelSearchState';
import {
  hasLensInboxParameters,
  hasSearchInInboxesParameters,
  InboxesParameters,
  InboxesSearchParameters,
  inboxSupportsRefinements,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  computeIsAllExceptSelection,
  findParametersInboxId,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {SearchInteractionOptions} from '../../../../store/stateless/searchActions';
import {InlineEmoji} from '../../../common/emoji/inlineEmoji';
import {CellsRegion} from '../../cells/cellsRegion';
import {CellsEmptySetupActionsVariantsEnum} from '../../cells/content/cellsEmpty/cellsEmptyCommon';
import {CellsEmptyTeammateSetupActions} from '../../cells/content/cellsEmpty/cellsEmptyTeammateSetupActions';
import {CellsEmptyTeammatesSetupActions} from '../../cells/content/cellsEmpty/cellsEmptyTeammatesSetupActions';
import {CellsEmptyTeamSetupActions} from '../../cells/content/cellsEmpty/cellsEmptyTeamSetupActions';
import {SandboxEmptyState} from '../../cells/content/cellsEmpty/sandboxEmptyState';
import {TeamInboxEmptyState} from '../../cells/content/cellsEmpty/teamInboxEmptyState';
import {SearchNoResults} from '../../search/noResults/searchNoResults';
import {EmptyInboxesAiTagging} from './emptyInboxesAiTagging';
import {StyledNoConversationsImg, StyledViewerBodyDiv, StyledViewerHeaderDiv} from './emptyInboxesStyles';
import {EmptyRefinedInboxesContainer} from './emptyRefinedInboxesContainer';

/*
 * Props.
 */

interface EmptyInboxesProps {
  panelId: CellsPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  shouldShowAddTeamInbox: boolean;
  ctaVariant: CellsEmptySetupActionsVariantsEnum | null;
  teamId?: number;
  selectedFilter?: InboxesFiltersEnum;
  selectedLens?: LensModel;
  hasRefinedResults: boolean;
  hasAssigneeLensCriteria: boolean;
  hasUnassignedFilter: boolean;
  selectedTeamDemoInbox?: InboxModel;
  navigate: (destination: string) => () => void;
  navigateToSearch: (
    searchParameters: InboxesSearchParameters,
    cellsContext?: CellsContext,
    options?: SearchInteractionOptions,
    sortProperty?: ConversationSortPropertiesEnum,
    querySuggestion?: CellsPanelSearchAppliedQuerySuggestion,
  ) => void;
  dismissCellsEmtpySetupActionsVariant: (task: StartGuideTaskTypesEnum) => () => void;
  didBecomeEmpty: boolean;
  isReviewingAiTags: boolean;
}

/*
 * Style.
 */

const StyledCellsRegion = styled(CellsRegion)`
  background-color: ${(p) => p.theme.appBackground};
  ${center()};
`;

const StyledContentDiv = styled.div<{
  $hasMaxWidth: boolean;
}>`
  text-align: center;
  ${(p) =>
    p.$hasMaxWidth &&
    css`
      max-width: 350px;
    `};
`;

interface ImageDimensions {
  width: number;
  height: number;
}

const StyledInboxZeroImg = styled.img<ImageDimensions>`
  width: ${(p) => p.width}px;
  height: ${(p) => p.height}px;
`;

const StyledInboxZeroCustomViewImg = styled.img`
  width: 380px;
  height: 240px;
`;

const StyledInboxZeroVideo = styled.video`
  // Disable pointer events here so that users won't be able to access the video controls
  pointer-events: none;
  width: 480px;
  height: 395px;
  object-fit: cover;
`;

/*
 * Component.
 */

export const EmptyInboxes: FC<EmptyInboxesProps> = (props) => {
  const {
    panelId,
    cellsContext,
    inboxesParameters,
    teamId,
    selectedFilter,
    selectedLens,
    selectedTeamDemoInbox,
    navigate,
    dismissCellsEmtpySetupActionsVariant,
    shouldShowAddTeamInbox,
    ctaVariant,
    didBecomeEmpty,
    hasRefinedResults,
    hasAssigneeLensCriteria,
    hasUnassignedFilter,
    isReviewingAiTags,
  } = props;
  const inboxId = findParametersInboxId(inboxesParameters);
  const theme = useUserTheme();
  const inboxZeroId = useConstant(() => Math.floor(Math.random() * 2));
  const localize = useLocalize();
  const isCustomViewLoading = useAppSelector(getIsBackfillingCustomView);
  const prefersReducedMotion = globalWindow.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // There aren't animated images for dark mode.
  const shouldShowAnimation = !prefersReducedMotion && didBecomeEmpty && !isExpandedDarkTheme(theme);
  const {namespace} = cellsContext;

  function renderDefaultContent() {
    if (hasUnassignedFilter && hasAssigneeLensCriteria) {
      return (
        <>
          <StyledNoConversationsImg
            draggable={false}
            src={inboxZero}
            alt={localize(
              defineMessage({
                id: 'empty-viewer-has-unassigned-filter-and-assignee-lens-criteria',
                message: 'No conversations',
              }),
            )}
          />
          <StyledViewerHeaderDiv>
            <Trans id="empty-unassigned-filter-with-assignee-lens-criteria-header">No conversations</Trans>
          </StyledViewerHeaderDiv>
          <StyledViewerBodyDiv>
            <Trans id="empty-unassigned-filter-with-assignee-lens-criteria-body">
              This view has an assignee filter. All open conversations will be found in the
              &ldquo;Assigned&rdquo; tab.
            </Trans>
          </StyledViewerBodyDiv>
        </>
      );
    }

    // Avoid showing the "inbox zero" messaging for the edge case where we have an "all except" selection that resolves
    // to no conversations in P2 being selected.
    if (!shouldShowInboxZero(cellsContext) || computeIsAllExceptSelection(inboxesParameters.selection)) {
      return (
        <>
          <StyledNoConversationsImg
            draggable={false}
            src={inboxZero}
            alt={localize(
              defineMessage({
                id: 'viewer-inboxzero',
                message: 'Empty inbox',
              }),
            )}
          />
          <StyledViewerHeaderDiv>
            <Trans id="empty-viewer-inboxzero">No conversations</Trans>
          </StyledViewerHeaderDiv>
        </>
      );
    }

    if (cellsContext.type === CellsContextTypesEnum.CUSTOM_VIEW && isCustomViewLoading) {
      return (
        <>
          <StyledInboxZeroCustomViewImg
            draggable={false}
            src={inboxZeroCustomViews}
            alt={localize(
              defineMessage({
                id: 'viewer-inboxzero-customviews',
                message: 'Empty view',
              }),
            )}
          />
          <StyledViewerHeaderDiv>
            <Trans id="empty-viewer-header-custom-view">Your views are loading</Trans>
          </StyledViewerHeaderDiv>
          <StyledViewerBodyDiv>
            <Trans id="empty-viewer-body-custom-view">
              Check back again later <InlineEmoji emoji={emojiMap.hugging_face} size={14} />
            </Trans>
          </StyledViewerBodyDiv>
        </>
      );
    }

    if (inboxZeroId === 1) {
      const inboxZero1 = findThemedImageInfo(theme, ThemedImageNamesEnum.INBOX_ZERO_1);
      return (
        <>
          {shouldShowAnimation ? (
            <StyledInboxZeroVideo
              autoPlay
              playsInline
              muted
              poster={inboxZeroPoster1}
              height={395}
              width={480}
            >
              <source src={inboxZeroAnimatedMov1} type='video/mp4; codecs="hvc1"' />
              <source src={inboxZeroAnimatedWebm1} type="video/webm" />
              <source src={inboxZeroAnimated1} type="video/mp4" />
            </StyledInboxZeroVideo>
          ) : (
            <StyledInboxZeroImg
              draggable={false}
              src={inboxZero1.src}
              height={inboxZero1.height}
              width={inboxZero1.width}
              alt={localize(
                defineMessage({
                  id: 'viewer-inboxzero1',
                  message: 'Empty inbox',
                }),
              )}
            />
          )}
          <StyledViewerHeaderDiv>
            <Trans id="empty-viewer-header1">Mission accomplished!</Trans>
          </StyledViewerHeaderDiv>
          <StyledViewerBodyDiv>
            <Trans id="empty-viewer-body1">
              Sit back and relax&hellip;
              <br />
              until the next message <InlineEmoji emoji={emojiMap.wink} size={14} />
            </Trans>
          </StyledViewerBodyDiv>
        </>
      );
    }

    const inboxZero2 = findThemedImageInfo(theme, ThemedImageNamesEnum.INBOX_ZERO_2);
    return (
      <>
        {shouldShowAnimation ? (
          <StyledInboxZeroVideo autoPlay playsInline muted poster={inboxZeroPoster2} height={395} width={480}>
            <source src={inboxZeroAnimatedMov2} type='video/mp4; codecs="hvc1"' />
            <source src={inboxZeroAnimatedWebm2} type="video/webm" />
            <source src={inboxZeroAnimated2} type="video/mp4" />
          </StyledInboxZeroVideo>
        ) : (
          <StyledInboxZeroImg
            draggable={false}
            src={inboxZero2.src}
            height={inboxZero2.height}
            width={inboxZero2.width}
            alt={localize(
              defineMessage({
                id: 'viewer-inboxzero2',
                message: 'Empty inbox',
              }),
            )}
          />
        )}
        <StyledViewerHeaderDiv>
          <Trans id="empty-viewer-header2">Way to go!</Trans>
        </StyledViewerHeaderDiv>
        <StyledViewerBodyDiv>
          {hasLensInboxParameters(inboxesParameters) ? (
            <Trans id="empty-viewer-body2-lens">
              Your view is clear <InlineEmoji emoji={emojiMap.raised_hands} size={14} />
              <br />
              Keep up the good work!
            </Trans>
          ) : (
            <Trans id="empty-viewer-body2">
              Your inbox is clear <InlineEmoji emoji={emojiMap.raised_hands} size={14} />
              <br />
              Keep up the good work!
            </Trans>
          )}
        </StyledViewerBodyDiv>
      </>
    );
  }

  function renderContent() {
    // Note: we should always have a selectedLens when isReviewingAiTags is true.
    if (isReviewingAiTags && selectedLens) {
      return (
        <EmptyInboxesAiTagging
          lensNamespace={selectedLens.namespace}
          lensId={selectedLens.id}
          reviewingAiTagId={selectedLens.reviewingAiTagId}
        />
      );
    }

    switch (ctaVariant) {
      case CellsEmptySetupActionsVariantsEnum.TEAMMATE: {
        return (
          <CellsEmptyTeammateSetupActions
            type={ctaVariant}
            navigate={navigate}
            namespace={namespace}
            shouldShowAddTeamInbox={shouldShowAddTeamInbox}
            teamId={teamId}
            selectedFilter={selectedFilter}
            onDismissClick={dismissCellsEmtpySetupActionsVariant(
              StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATE_DISMISS,
            )}
          />
        );
      }
      case CellsEmptySetupActionsVariantsEnum.TEAM: {
        return (
          <CellsEmptyTeamSetupActions
            type={ctaVariant}
            navigate={navigate}
            namespace={namespace}
            onDismissClick={dismissCellsEmtpySetupActionsVariant(
              StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAM_DISMISS,
            )}
            selectedTeamDemoInbox={selectedTeamDemoInbox}
          />
        );
      }
      case CellsEmptySetupActionsVariantsEnum.TEAMMATES: {
        return (
          <CellsEmptyTeammatesSetupActions
            type={ctaVariant}
            navigate={navigate}
            namespace={namespace}
            onDismissClick={dismissCellsEmtpySetupActionsVariant(
              StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATES_DISMISS,
            )}
          />
        );
      }
      case CellsEmptySetupActionsVariantsEnum.TEAM_CHANNEL: {
        return <TeamInboxEmptyState namespace={namespace} inboxId={inboxId} />;
      }
      case CellsEmptySetupActionsVariantsEnum.SANDBOX: {
        return <SandboxEmptyState inboxesParameters={inboxesParameters} />;
      }
      default: {
        // Return early if we don't need to display a CTA to search in other search filters.
        if (!hasSearchInInboxesParameters(inboxesParameters)) {
          return renderDefaultContent();
        }

        return (
          <SearchNoResults
            panelId={panelId}
            parameters={inboxesParameters}
            cellsContext={cellsContext}
            navigateToSearch={props.navigateToSearch}
          />
        );
      }
    }
  }

  // Due to the cellsRefinementOptions button header, we have to special case the layout to "center" relative to the
  // parent element.
  if (inboxSupportsRefinements(inboxesParameters) && hasRefinedResults) {
    return <EmptyRefinedInboxesContainer panelId={panelId} parameters={inboxesParameters} />;
  }

  return (
    <StyledCellsRegion>
      <StyledContentDiv
        {...buildInteractionIdProps('empty-inboxes')}
        $hasMaxWidth={Boolean(ctaVariant) && ctaVariant !== CellsEmptySetupActionsVariantsEnum.TEAM_CHANNEL}
      >
        {renderContent()}
      </StyledContentDiv>
    </StyledCellsRegion>
  );
};

function shouldShowInboxZero(cellsContext: CellsContext) {
  const {
    inDrafts,
    inSent,
    isDelegation,
    isAssignedShared,
    inOpenFilter,
    searchQuery,
    isCellsEmptySetupActionsActive,
  } = cellsContext;

  return (
    inOpenFilter &&
    !inDrafts &&
    !inSent &&
    !isDelegation &&
    !isAssignedShared &&
    !searchQuery &&
    !isCellsEmptySetupActionsActive
  );
}
